/**
 * Every capability the platform can grant.
 *
 * Permissions are the ONLY thing route handlers and UI guards check. Roles map
 * to permission sets (below); the mapping is data, not logic, so new roles or
 * capabilities can be introduced without touching call sites.
 *
 * Naming: `<resource>:<action>[:<scope>]`.
 */
export const PERMISSIONS = [
    // ---- Platform access -------------------------------------------------
    'platform:access',
    // ---- Tasks -----------------------------------------------------------
    'task:create',
    /** Create a task on behalf of / assigned to somebody else. */
    'task:create:assign_other',
    /** Read tasks where the actor is creator or assignee. */
    'task:read:own',
    /** Read tasks belonging to the wider development team (non-confidential). */
    'task:read:team',
    /** Read every task in the organisation, including archived and deleted. */
    'task:read:all',
    'task:update:own',
    'task:update:any',
    /** Move a task through the workflow when the actor owns it. */
    'task:transition:own',
    /** Move any task through the workflow regardless of ownership. */
    'task:transition:any',
    'task:assign',
    /** Raise a task to CRITICAL priority. Deliberately restricted (see §15). */
    'task:priority:critical',
    'task:cancel',
    'task:archive',
    /** Soft delete a task. */
    'task:delete',
    'task:restore',
    /** See soft-deleted tasks. */
    'task:read:deleted',
    // ---- Conversation ----------------------------------------------------
    'comment:create',
    'comment:edit:own',
    'comment:delete:own',
    'comment:delete:any',
    // ---- Attachments -----------------------------------------------------
    'attachment:upload',
    'attachment:delete:own',
    'attachment:delete:any',
    // ---- Review ----------------------------------------------------------
    'review:queue:read',
    'review:approve',
    'review:request_changes',
    // ---- Leadership Requests ("tickets") ----------------------------------
    /** File a new Leadership Request. Department Leadership only. */
    'ticket:create',
    /** Read the requests a Department Leadership account itself opened. */
    'ticket:read:own',
    /**
     * Read requests visible to the wider development team: every NEW
     * (unclaimed) request, plus any request the actor is personally assigned
     * to. A claimed request stops being visible under this permission to
     * anyone else — that narrowing is what "claiming" means (see
     * `ticket-workflow.ts`).
     */
    'ticket:read:team',
    /** Read every Leadership Request regardless of state or assignment. */
    'ticket:read:all',
    /** Claim (or reassign) a request to a developer. Director only. */
    'ticket:assign',
    /** Mark a request complete after its linked work is signed off. */
    'ticket:complete',
    /** Force-close a request at any time, for any reason, done or not. */
    'ticket:deny',
    'ticket:comment:create',
    /**
     * Kept distinct from the task comment permissions (`comment:edit:own` etc.)
     * on purpose: Department Leadership holds these without ever touching a
     * task comment, and a director's ability to moderate one conversation must
     * not silently imply the other.
     */
    'ticket:comment:edit:own',
    'ticket:comment:delete:own',
    'ticket:comment:delete:any',
    // ---- People ----------------------------------------------------------
    'user:read',
    'user:manage',
    // ---- Analytics -------------------------------------------------------
    'analytics:read:self',
    'analytics:read:team',
    'analytics:read:all',
    // ---- Administration --------------------------------------------------
    'audit:read',
    'settings:read',
    'settings:manage',
    'reference:manage',
    'notification:read:own',
    'notification:read:all',
];
export function isPermission(value) {
    return typeof value === 'string' && PERMISSIONS.includes(value);
}
/**
 * A Department Leadership account is deliberately isolated: it can only file
 * and follow its own Leadership Requests, never anything on the development
 * side (no task list, no analytics, no team, no settings).
 */
const DEPARTMENT_LEAD_PERMISSIONS = [
    'platform:access',
    'ticket:create',
    'ticket:read:own',
    'ticket:comment:create',
    'ticket:comment:edit:own',
    'ticket:comment:delete:own',
];
const DEVELOPER_PERMISSIONS = [
    'platform:access',
    'task:create',
    'task:read:own',
    'task:read:team',
    'task:update:own',
    'task:transition:own',
    'comment:create',
    'comment:edit:own',
    'comment:delete:own',
    'attachment:upload',
    'attachment:delete:own',
    'user:read',
    'analytics:read:self',
    'settings:read',
    'notification:read:own',
    'ticket:read:team',
    'ticket:comment:create',
    'ticket:comment:edit:own',
    'ticket:comment:delete:own',
];
const SENIOR_DEVELOPER_PERMISSIONS = [
    ...DEVELOPER_PERMISSIONS,
    'task:create:assign_other',
    'task:read:all',
    'task:update:any',
    'task:assign',
    'task:priority:critical',
    'review:queue:read',
    'analytics:read:team',
];
const DIRECTOR_PERMISSIONS = [
    ...SENIOR_DEVELOPER_PERMISSIONS,
    'task:transition:any',
    'task:cancel',
    'task:archive',
    'task:delete',
    'task:restore',
    'task:read:deleted',
    'comment:delete:any',
    'attachment:delete:any',
    'review:approve',
    'review:request_changes',
    'user:manage',
    'analytics:read:all',
    'audit:read',
    'settings:manage',
    'reference:manage',
    'notification:read:all',
    'ticket:read:all',
    'ticket:assign',
    'ticket:complete',
    'ticket:deny',
    'ticket:comment:delete:any',
];
/**
 * Role → permission mapping. This is the whole authorisation model; extend it
 * (or a future database-backed override) rather than adding role checks in
 * business logic.
 */
export const ROLE_PERMISSIONS = {
    NONE: [],
    DEPARTMENT_LEAD: dedupe(DEPARTMENT_LEAD_PERMISSIONS),
    DEVELOPER: dedupe(DEVELOPER_PERMISSIONS),
    SENIOR_DEVELOPER: dedupe(SENIOR_DEVELOPER_PERMISSIONS),
    DIRECTOR: dedupe(DIRECTOR_PERMISSIONS),
};
function dedupe(list) {
    return Object.freeze([...new Set(list)]);
}
export function permissionsForRole(role) {
    return ROLE_PERMISSIONS[role] ?? [];
}
export function roleHasPermission(role, permission) {
    return permissionsForRole(role).includes(permission);
}
/** Convenience for UI guards: does this permission set contain them all? */
export function hasAll(granted, required) {
    return required.every((p) => granted.includes(p));
}
/** Convenience for UI guards: does this permission set contain any of them? */
export function hasAny(granted, required) {
    return required.some((p) => granted.includes(p));
}
//# sourceMappingURL=permissions.js.map